import React from 'react'
import styled from 'styled-components'
import IconOddleEatsVoucher from '@jarvis.oddle/core/components/Icons/IconOddleEatsVoucher'
import {
  borderRadius,
  borderRadiusSizes,
  buttonShapes,
  colorConstants,
  margin,
  marginSizes
} from '@jarvis.oddle/core/theme/default'
import { Column, Row } from '@jarvis.oddle/core/components/Grid'
import {
  H3,
  H4,
  LinkText,
  P,
  SmallText
} from '@jarvis.oddle/core/components/Typo'
import Button from '@jarvis.oddle/core/components/Button'
import { Section } from 'src/components/molecules/CheckoutComponents/Elements'
import breakpoint from 'src/utils/styledComponentsBreakpoint'
import { isObjectEmpty } from 'src/utils/object'
import { isEmpty } from 'src/utils/string'
import Flex from '@jarvis.oddle/core/components/Flex'
import { TranslationContext } from '@jarvis.oddle/core/components/Translation'
import { getVoucherTitle } from 'tools/voucher'

const StyledCard = styled(Section)`
  width: 100%;
  border-radius: ${borderRadius[borderRadiusSizes.medium]};
  border: 1px solid
    ${(props) =>
      props.hasError
        ? colorConstants.red
        : props.selectedVoucher
        ? colorConstants.blue
        : colorConstants.lightGray};
  margin-right: ${margin[marginSizes.none]} !important;
  margin-left: ${margin[marginSizes.none]} !important;
  background: linear-gradient(92.19deg, #f7fdff 17.33%, #f3f4ff 82.04%);
  position: relative;
  ${breakpoint('tablet')`
    display: none;
  `}
`

const StyledIcon = styled(Column)`
  &&& {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    max-width: 110px;
  }
`

const StyledContent = styled(Column)`
  text-align: left;
  border-left: 1px dashed #bbb;
`

const StyledDetails = styled(Column)`
  &&& {
    display: flex;
    align-self: center;
    justify-content: center;
    text-align: center;
  }
`

const StyledButton = styled(Button).attrs(() => ({
  buttonColor: '#0A3040',
  buttonShape: buttonShapes.round
}))`
  background-color: ${colorConstants.white};
  box-shadow: none;
  border: 1px solid #0a3040;

  &:hover,
  &:focus {
    background-color: ${colorConstants.white};
    box-shadow: none;
    border: 2px solid #0a3040;
  }
`

const StyledButtonText = styled(H4).attrs(() => ({
  normalised: true,
  bold: true
}))`
  color: #0a3040;
  white-space: nowrap;
`

const StyledRow = styled(Row)`
  &::after,
  &::before {
    content: '';
    display: block;
    background-color: ${(props) => props.theme.colorContainerColor};
    position: absolute;
    z-index: 1;
    width: 16px;
    height: 32px;
    top: calc(50% - 34px / 2 - 0.5px);
    border-top-left-radius: 32px;
    border-bottom-left-radius: 32px;
  }

  &::before {
    left: -1px;
    transform: rotate(180deg);
    border: 1px solid
      ${(props) =>
        props.hasError
          ? colorConstants.red
          : props.selectedVoucher
          ? colorConstants.blue
          : colorConstants.lightGray};
    border-right: none;
  }

  &::after {
    right: -1px;
    border: 1px solid
      ${(props) =>
        props.hasError
          ? colorConstants.red
          : props.selectedVoucher
          ? colorConstants.blue
          : colorConstants.lightGray};
    border-right: none;
  }
`

const StyledLinkText = styled(LinkText)`
  &&& {
    font-family: Roboto, serif;
    font-size: 13px;
    color: ${colorConstants.mainVoucherColor};
  }
`

const StyledChangeVoucher = styled(Flex)`
  position: absolute;
  bottom: 0;
`

const VoucherTicket = React.memo(
  ({
    defaultTitle,
    defaultDescription,
    selectedVoucher = {},
    errors,
    onClickViewVouchers,
    removeSelectedVoucherAction
  }: {
    defaultTitle: string
    defaultDescription: string
    selectedVoucher: {
      id?: string
      code?: string
      redeemedDate?: number
    }
    errors: string
    onClickViewVouchers: () => void
    removeSelectedVoucherAction: () => void
  }) => {
    const { t } = React.useContext(TranslationContext)

    return (
      <StyledCard
        hasError={!isEmpty(errors)}
        selectedVoucher={!isObjectEmpty(selectedVoucher)}
      >
        <StyledRow
          hasError={!isEmpty(errors)}
          selectedVoucher={!isObjectEmpty(selectedVoucher)}
        >
          {!isObjectEmpty(selectedVoucher) && (
            <React.Fragment>
              <StyledIcon sm={4}>
                <IconOddleEatsVoucher width='100%' height='100%' />
              </StyledIcon>
              <StyledContent sm={8}>
                <H3
                  bold
                  fontFamily='Roboto'
                  marginTop={marginSizes.extraSmall}
                  color={colorConstants.mainVoucherColor}
                >
                  {getVoucherTitle(selectedVoucher)}
                </H3>
                <StyledChangeVoucher
                  width='120px'
                  justifyContent='space-between'
                  marginBottom={marginSizes.extraSmall}
                >
                  <StyledLinkText heading onClick={removeSelectedVoucherAction}>
                    {t('Remove')}
                  </StyledLinkText>
                  <SmallText
                    normalised
                    fontFamily='Roboto'
                    color={colorConstants.mainVoucherColor}
                  >
                    |
                  </SmallText>
                  <StyledLinkText heading onClick={onClickViewVouchers}>
                    {t('Change')}
                  </StyledLinkText>
                </StyledChangeVoucher>
              </StyledContent>
            </React.Fragment>
          )}
          {isObjectEmpty(selectedVoucher) && (
            <React.Fragment>
              <StyledIcon sm={6}>
                <IconOddleEatsVoucher width='100%' height='100%' />
              </StyledIcon>
              <StyledContent sm={6}>
                <H3
                  bold
                  fontFamily='Roboto'
                  color={colorConstants.mainVoucherColor}
                >
                  {defaultTitle}
                </H3>
              </StyledContent>
              <StyledDetails sm={12}>
                <P fontFamily='Roboto' color={colorConstants.mainVoucherColor}>
                  {defaultDescription}
                </P>
              </StyledDetails>
              <Column sm={12}>
                <StyledButton
                  fullWidth
                  ButtonText={StyledButtonText}
                  onClick={onClickViewVouchers}
                >
                  {() => (
                    <H4
                      normalised
                      bold
                      fontFamily='Roboto'
                      color={colorConstants.mainVoucherColor}
                    >
                      {t('View Vouchers')}
                    </H4>
                  )}
                </StyledButton>
              </Column>
            </React.Fragment>
          )}
        </StyledRow>
      </StyledCard>
    )
  }
)

export default VoucherTicket
